import React, { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, CircularProgress, Button, Container } from "@mui/material";
import { RootState, AppDispatch } from "../../store/store";
import {
    fetchFullSearchResults,
    resetFullSearch,
    UserSearchResult,
    PostSearchResult,
} from "../../store/slices/searchSlice";
import classes from "./SearchResultsPage.module.scss";

// Placeholder components for UserCard and PostCard
// In a real scenario, these would be imported from their respective component paths
const PlaceholderUserCard: React.FC<{ user: UserSearchResult }> = ({ user }) => (
    <Box className={classes.userCardPlaceholder}>
        <Typography variant="subtitle1">{user.fullName}</Typography>
        {/* Add more user details here based on UserSearchResult type */}
    </Box>
);

const PlaceholderPostCard: React.FC<{ post: PostSearchResult }> = ({ post }) => (
    <Box className={classes.postCardPlaceholder}>
        <Typography variant="subtitle1">Post ID: {post.postId}</Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
            {post.content.substring(0, 100)}...
        </Typography>
        {/* Add more post details here based on PostSearchResult type */}
    </Box>
);

const SearchResultsPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();

    const query = searchParams.get("q");
    const type = searchParams.get("type") as "posts" | "users" | null;

    const {
        results,
        isLoadingFullSearch,
        fullSearchError,
        currentUserLastKey,
        currentPostLastKey,
    } = useSelector((state: RootState) => state.search);

    const currentResults = useMemo(() => {
        if (!type) return [];
        return type === "users" ? results.users : results.posts;
    }, [results, type]);

    const currentLastKey = useMemo(() => {
        if (!type) return null;
        return type === "users" ? currentUserLastKey : currentPostLastKey;
    }, [currentUserLastKey, currentPostLastKey, type]);

    useEffect(() => {
        if (query && type) {
            console.log(
                `[SearchResultsPage] Initial search or params changed. Query: "${query}", Type: "${type}". Resetting and fetching.`,
            );
            dispatch(resetFullSearch());
            dispatch(fetchFullSearchResults({ query, type, exclusiveStartKey: null }))
                .unwrap()
                .then((data) => {
                    console.log(
                        `[SearchResultsPage] Initial fetchFullSearchResults successful for query: "${query}", type: "${type}". Received:`,
                        data,
                    );
                })
                .catch((error) => {
                    console.error(
                        `[SearchResultsPage] Initial fetchFullSearchResults failed for query: "${query}", type: "${type}". Error:`,
                        error,
                    );
                });
        }
    }, [dispatch, query, type]);

    const handleLoadMore = () => {
        if (query && type && currentLastKey) {
            const keyToUse = type === "users" ? currentUserLastKey : currentPostLastKey;
            console.log(
                `[SearchResultsPage] Handling "Load More". Query: "${query}", Type: "${type}", Key: "${keyToUse || ""}"`,
            );
            dispatch(
                fetchFullSearchResults({
                    query,
                    type,
                    exclusiveStartKey: keyToUse,
                }),
            )
                .unwrap()
                .then((data) => {
                    console.log(
                        `[SearchResultsPage] "Load More" fetchFullSearchResults successful. Query: "${query}", Type: "${type}", Key: "${keyToUse || ""}". Received:`,
                        data,
                    );
                })
                .catch((error) => {
                    console.error(
                        `[SearchResultsPage] "Load More" fetchFullSearchResults failed. Query: "${query}", Type: "${type}", Key: "${keyToUse || ""}". Error:`,
                        error,
                    );
                });
        }
    };

    if (!query || !type) {
        return (
            <Container className={classes.pageContainer}>
                <Typography variant="h5" className={classes.messageText}>
                    Search query or type is missing. Please try searching again.
                </Typography>
            </Container>
        );
    }

    return (
        // Main Page Container
        <Container className={classes.pageContainer}>
            {/* Header: "Search results for..." */}
            <Typography variant="h4" component="h1" className={classes.resultsHeader}>
                Search results for &quot;{query}&quot; in {type === "users" ? "Users" : "Posts"}
            </Typography>

            {/* Loading Spinner (Initial Load) */}
            {isLoadingFullSearch && currentResults.length === 0 && (
                <Box className={classes.loaderContainer}>
                    <CircularProgress />
                </Box>
            )}

            {/* Error Message Area */}
            {fullSearchError && (
                <Typography color="error" className={classes.messageText}>
                    Error: {fullSearchError}
                </Typography>
            )}

            {/* "No Results Found" Message */}
            {!isLoadingFullSearch && !fullSearchError && currentResults.length === 0 && (
                <Typography className={classes.messageText}>
                    No results found for &quot;{query}&quot;.
                </Typography>
            )}

            {/* Search Results Display Area */}
            {currentResults.length > 0 && (
                <Box
                    className={
                        type === "users" ? classes.resultsGridUsers : classes.resultsListPosts
                    }
                >
                    {/* User Search Results (Placeholder Cards) */}
                    {type === "users" &&
                        (currentResults as UserSearchResult[]).map((user) => (
                            <PlaceholderUserCard key={user.userId} user={user} />
                            // Replace with <UserCard key={user.userId} user={user} />
                        ))}
                    {/* Post Search Results (Placeholder Cards) */}
                    {type === "posts" &&
                        (currentResults as PostSearchResult[]).map((post) => (
                            <PlaceholderPostCard key={post.postId} post={post} />
                            // Replace with <PostCard key={post.postId} post={post} />
                        ))}
                </Box>
            )}

            {/* "Load More" Button Area */}
            {currentLastKey && !isLoadingFullSearch && (
                <Box className={classes.loadMoreButtonContainer}>
                    <Button
                        variant="contained"
                        onClick={handleLoadMore}
                        className={classes.loadMoreButton}
                        disabled={isLoadingFullSearch} // Disable while loading next page too
                    >
                        {isLoadingFullSearch ? "Loading..." : "Load More"}
                    </Button>
                </Box>
            )}
            {/* Loading Spinner (When loading more results) */}
            {isLoadingFullSearch && currentResults.length > 0 && (
                <Box className={classes.loaderContainer}>
                    <CircularProgress />
                </Box>
            )}
        </Container>
    );
};

export default SearchResultsPage;
